import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Users,
  Plus,
  CreditCard,
  Envelope,
  User,
  GraduationCap,
  CurrencyDollar,
  IdentificationCard,
  Drop,
  ArrowRight,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import { DataPagination } from '../components/DataPagination';

const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'N/A', label: 'N/A' },
];

const SUBSCRIPTION_PLANS = [
  { value: 'Fee', label: 'Fee' },
  { value: 'Free', label: 'Free' },
];

const initialFormData = {
  sub_name: '',
  card_no: '',
  mobile: '',
  email: '',
  gender: 'N/A',
  entity_id: '',
  campus_id: '',
  category: 'STUDENT',
  student_id: '',
  student_course_id: '',
  curr_year_of_study: '',
  subscription_plan: 'Fee',
  sub_fee_amount: '',
  recharge: '',
  is_ambassador_reg: 'N',
  min_recharge_limit: '',
  id_type: '1',
  id_number: '',
  relationship_agent: '',
  is_waba_reg: 'N',
  wa_reg_no: '',
  waba_reg_code: '',
};

const formSteps = [
  { label: 'Personal', icon: User },
  { label: 'Assignment', icon: GraduationCap },
  { label: 'Subscription', icon: CurrencyDollar },
  { label: 'Identification', icon: IdentificationCard },
];

export default function SubscribersPage() {
  const { isSuperAdmin } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [idTypes, setIdTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const fetchIdTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/subscriber-id-types`, { withCredentials: true, headers });
      setIdTypes(data);
    } catch (error) {
      console.error('Error fetching ID types:', error);
    }
  }, []);

  useEffect(() => { fetchIdTypes(); }, [fetchIdTypes]);

  const fetchCategories = useCallback(async (entityId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/subscriber-categories`;
      if (entityId && entityId !== 'all') url += `?entity_id=${entityId}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => { fetchCategories(filterEntity); }, [filterEntity, fetchCategories]);

  const fetchCampuses = useCallback(async (entityId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/campuses`;
      if (entityId && entityId !== 'all') url += `?entity_id=${entityId}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCampuses(data);
    } catch (error) {
      console.error('Error fetching campuses:', error);
    }
  }, []);

  const fetchCards = useCallback(async (entityId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/cards`;
      if (entityId && entityId !== 'all') url += `?entity_id=${entityId}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCards(data.filter(c => c.occupied === 'N'));
    } catch (error) {
      console.error('Error fetching cards:', error);
    }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/subscribers`;
      const params = [];
      if (filterEntity && filterEntity !== 'all') params.push(`entity_id=${filterEntity}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setSubscribers(data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error(formatApiError(error, 'Failed to fetch subscribers'));
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);
  useEffect(() => { fetchCampuses(filterEntity); }, [filterEntity, fetchCampuses]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ ...initialFormData, entity_id: selectedEntity?.id || '' });
    setEditingSubscriber(null);
    setStep(0);
  };

  const openCreateDialog = () => {
    resetForm();
    const eid = selectedEntity?.id || '';
    if (eid) {
      fetchCampuses(eid);
      fetchCards(eid);
      fetchCategories(eid);
    }
    setIsDialogOpen(true);
  };

  const openEditDialog = (subscriber) => {
    setEditingSubscriber(subscriber);
    setStep(0);
    setFormData({
      ...initialFormData,
      sub_name: subscriber.sub_name || `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim(),
      card_no: subscriber.card_no || '',
      mobile: subscriber.mobile || '',
      email: subscriber.email || '',
      gender: subscriber.gender || 'N/A',
      entity_id: subscriber.entity_id || '',
      campus_id: subscriber.campus_id || '',
      category: subscriber.category || 'STUDENT',
      student_id: subscriber.student_id || '',
      id_type: subscriber.id_type || '1',
      id_number: subscriber.id_number || '',
      subscription_plan: subscriber.subscription_plan || 'Fee',
    });
    fetchCampuses(subscriber.entity_id);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingSubscriber) {
        const updatePayload = {
          sub_name: formData.sub_name,
          email: formData.email,
          mobile: formData.mobile,
          gender: formData.gender,
          campus_id: formData.campus_id,
          category: formData.category,
          id_type: formData.id_type,
          id_number: formData.id_number,
        };
        await axios.put(`${API_URL}/api/subscribers/${editingSubscriber.id}`, updatePayload, {
          withCredentials: true, headers
        });
        toast.success('Subscriber updated successfully');
      } else {
        await axios.post(`${API_URL}/api/subscribers`, formData, {
          withCredentials: true, headers
        });
        toast.success('Subscriber created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchSubscribers();
    } catch (error) {
      const message = formatApiError(error, 'Failed to save subscriber');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (subscriber) => {
    const name = subscriber.sub_name || `${subscriber.first_name} ${subscriber.last_name}`;
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL}/api/subscribers/${subscriber.id}`, {
        withCredentials: true, headers
      });
      toast.success('Subscriber deleted successfully');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  // Performance tier — derived from upstream `sub_status` code:
  //   E → Excellent, G → Good, N (or blank/active) → Normal, D → Dormant
  // Waba-pending (registration without card yet) is surfaced as its own tier.
  // This is the single source of truth used by both the filter tabs and the
  // pill in each row.
  const perfTierOf = (sub) => {
    const isWabaPending = (sub.is_waba_reg === 'Y' && !sub.card_no);
    if (isWabaPending) return 'pending';
    const code = String(sub.status || sub.sub_status || '').trim().toUpperCase().slice(0, 1);
    if (code === 'E') return 'excellent';
    if (code === 'G') return 'good';
    if (code === 'D') return 'dormant';
    return 'normal'; // "N" + legacy "active" + blank
  };

  const filteredSubscribers = subscribers.filter(sub => {
    const name = sub.sub_name || `${sub.first_name || ''} ${sub.last_name || ''}`;
    const matchesSearch =
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.mobile || '').includes(searchQuery) ||
      (sub.card_no || '').includes(searchQuery);
    const matchesCategory = filterCategory === 'all' || sub.category === filterCategory;

    const perf = perfTierOf(sub);

    let matchesTab = true;
    if (filterStatus === 'students')      matchesTab = (sub.category || '').toUpperCase() === 'STUDENT';
    else if (filterStatus === 'excellent') matchesTab = perf === 'excellent';
    else if (filterStatus === 'good')      matchesTab = perf === 'good';
    else if (filterStatus === 'normal')    matchesTab = perf === 'normal';
    else if (filterStatus === 'dormant')   matchesTab = perf === 'dormant';

    return matchesSearch && matchesCategory && matchesTab;
  });

  // Pagination (client-side over filtered list)
  const totalFiltered = filteredSubscribers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedSubscribers = filteredSubscribers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );
  const pageStart = totalFiltered === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const pageEnd = Math.min(safePage * pageSize, totalFiltered);

  // Reset to page 1 whenever filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterCategory, filterEntity, filterStatus]);

  const isStudent = formData.category === 'STUDENT';

  return (
    <div className="space-y-3 pb-6" data-testid="subscribers-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Subscribers
        </h1>
      </div>

      {/* Sticky toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name, card, phone, email…"
        testId="subs-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterCategory('all'); setFilterEntity('all'); setFilterStatus('all'); }}
        filters={[
          ...(isSuperAdmin ? [{
            key: 'entity',
            label: 'Entity',
            value: filterEntity,
            onChange: setFilterEntity,
            options: [{ value: 'all', label: 'All entities' }, ...entities.map(e => ({ value: e.id, label: e.entity_name }))],
          }] : []),
          {
            key: 'category',
            label: 'Category',
            value: filterCategory,
            onChange: setFilterCategory,
            options: [{ value: 'all', label: 'All categories' }, ...categories.map(c => ({ value: c.category, label: c.category }))],
          },
        ]}
      />

      {/* Data table — pill rows */}
      {(() => {
        const perfOf = (sub) => {
          const tier = perfTierOf(sub);
          if (tier === 'pending')   return { label: 'Pending',   bg: 'bg-amber-100',   text: 'text-amber-800',   ring: 'ring-amber-300' };
          if (tier === 'excellent') return { label: 'Excellent', bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-300' };
          if (tier === 'good')      return { label: 'Good',      bg: 'bg-blue-100',    text: 'text-blue-800',    ring: 'ring-blue-300' };
          if (tier === 'normal')    return { label: 'Normal',    bg: 'bg-yellow-100',  text: 'text-yellow-800',  ring: 'ring-yellow-300' };
          return { label: 'Dormant', bg: 'bg-rose-100', text: 'text-rose-800', ring: 'ring-rose-300' };
        };
        const hasFilter = !!(searchQuery || filterCategory !== 'all' || filterEntity !== 'all' || filterStatus !== 'all');

        return (
          <DataTable
            columns={[
              { key: 'name', label: 'Name', width: 'minmax(180px,1.6fr)', render: (sub) => {
                const raw = sub.sub_name || `${sub.first_name || ''} ${sub.last_name || ''}`.trim() || 'Unknown';
                const name = raw.toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());
                return (
                  <span className="truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {name}
                  </span>
                );
              } },
              { key: 'card', label: 'Card', width: 'minmax(130px,0.9fr)', mono: true, tone: 'strong', render: (sub) => sub.card_no || '—' },
              { key: 'campus_name', label: 'Campus', width: 'minmax(220px,2.4fr)', tone: 'muted', wrap: true },
              { key: 'mobile',      label: 'Mobile', width: 'minmax(120px,0.9fr)', mono: true, tone: 'muted' },
              { key: 'category',    label: 'Category', width: 'minmax(100px,0.8fr)', render: (sub) => {
                const raw = String(sub.category || '—').toLowerCase();
                return raw.replace(/\b\w/g, ch => ch.toUpperCase());
              } },
              { key: 'perf', label: 'Performance', width: 'minmax(110px,0.9fr)', render: (sub) => {
                const perf = perfOf(sub);
                return (
                  <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full ring-1 ring-inset ${perf.bg} ${perf.text} ${perf.ring} text-[11px] font-semibold tracking-wide group-hover:bg-white/90 group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {perf.label}
                  </span>
                );
              } },
            ]}
            rows={paginatedSubscribers}
            loading={loading}
            rowKey={(sub) => sub.id}
            onRowClick={(sub) => navigate(`/subscribers/${sub.id}`)}
            emptyIcon={<Users size={20} className="text-slate-500" />}
            emptyTitle={hasFilter ? 'No matches' : 'No subscribers yet'}
            emptyText={hasFilter ? 'Try clearing filters or refining your search.' : 'Subscribers will appear here once registered.'}
            testIdPrefix="subscriber"
            actionRender={(sub) => (
              <span className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); navigate(`/subscribers/${sub.id}`); }}
                  className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
                  data-testid={`view-subscriber-${sub.id}`}
                  aria-label="View subscriber"
                >
                  <Eye size={14} />
                </Button>
              </span>
            )}
          />
        );
      })()}

      {/* Pagination */}
      <DataPagination
        page={safePage}
        pageSize={pageSize}
        total={totalFiltered}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
        testId="subs-pagination"
      />

      {/* Create/Edit Dialog — Multi-Step */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) setStep(0); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editingSubscriber ? 'Edit Subscriber' : 'Add Subscriber'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingSubscriber ? 'Edit subscriber details' : 'Register a new subscriber'}
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          {!editingSubscriber && (
            <div className="flex items-center justify-between mb-6 px-2">
              {formSteps.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                const isDone = i < step;
                return (
                  <div key={s.label} className="flex items-center flex-1 last:flex-none">
                    <button
                      type="button"
                      onClick={() => setStep(i)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive ? 'bg-[#0A2540] text-white'
                          : isDone ? 'bg-[#10B981]/10 text-[#10B981]'
                          : 'bg-[#FCFAF6] text-[#78716C]'
                      }`}
                      data-testid={`sub-step-${i}`}
                    >
                      <Icon size={14} />
                      <span className="hidden sm:inline">{s.label}</span>
                    </button>
                    {i < formSteps.length - 1 && (
                      <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[#10B981]/30' : 'bg-[#EDE6D8]'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 0: Personal Info */}
            {(step === 0 || editingSubscriber) && (
              <div className="space-y-4" data-testid="sub-step-personal">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Subscriber personal details.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name <span className="text-[#EF4444]">*</span></Label>
                    <Input value={formData.sub_name} onChange={(e) => updateField('sub_name', e.target.value)}
                      placeholder="e.g., RUSEKE JR" className="border-[#EDE6D8] rounded-lg" data-testid="sub-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => updateField('gender', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email <span className="text-[#EF4444]">*</span></Label>
                    <Input type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)}
                      placeholder="e.g., ruseke@test.com" className="border-[#EDE6D8] rounded-lg" data-testid="sub-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile <span className="text-[#EF4444]">*</span></Label>
                    <Input value={formData.mobile} onChange={(e) => updateField('mobile', e.target.value)}
                      placeholder="e.g., 256712871287" className="border-[#EDE6D8] rounded-lg" data-testid="sub-mobile-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Assignment (Entity, Campus, Card, Category) */}
            {step === 1 && !editingSubscriber && (
              <div className="space-y-4" data-testid="sub-step-assignment">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Assign to entity, campus, and card.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {isSuperAdmin && (
                    <div className="space-y-2">
                      <Label>Entity <span className="text-[#EF4444]">*</span></Label>
                      <Select value={formData.entity_id} onValueChange={(v) => {
                        updateField('entity_id', v);
                        updateField('campus_id', '');
                        updateField('card_no', '');
                        updateField('category', 'STUDENT');
                        fetchCampuses(v);
                        fetchCards(v);
                        fetchCategories(v);
                      }}>
                        <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-entity-select">
                          <SelectValue placeholder="Select entity" />
                        </SelectTrigger>
                        <SelectContent>
                          {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Campus <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.campus_id} onValueChange={(v) => updateField('campus_id', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-campus-select">
                        <SelectValue placeholder="Select campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.campus_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Card Number <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.card_no} onValueChange={(v) => updateField('card_no', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-card-select">
                        <SelectValue placeholder="Select available card" />
                      </SelectTrigger>
                      <SelectContent>
                        {cards.map(c => <SelectItem key={c.id} value={c.card_no}>{c.card_no}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.category} onValueChange={(v) => updateField('category', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}
                        {categories.length === 0 && <SelectItem value="STUDENT">STUDENT</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {isStudent && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Student ID <span className="text-[#EF4444]">*</span></Label>
                      <Input value={formData.student_id} onChange={(e) => updateField('student_id', e.target.value)}
                        placeholder="e.g., 34566ghfghgjh" className="border-[#EDE6D8] rounded-lg" data-testid="sub-student-id-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Course ID</Label>
                      <Input value={formData.student_course_id} onChange={(e) => updateField('student_course_id', e.target.value)}
                        placeholder="e.g., 2 (optional)" className="border-[#EDE6D8] rounded-lg" data-testid="sub-course-id-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Year of Study <span className="text-[#EF4444]">*</span></Label>
                      <Input value={formData.curr_year_of_study} onChange={(e) => updateField('curr_year_of_study', e.target.value)}
                        placeholder="e.g., 1" className="border-[#EDE6D8] rounded-lg" data-testid="sub-year-input" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Subscription & Recharge */}
            {step === 2 && !editingSubscriber && (
              <div className="space-y-4" data-testid="sub-step-subscription">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Subscription plan and recharge settings.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subscription Plan <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.subscription_plan} onValueChange={(v) => updateField('subscription_plan', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-plan-select">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBSCRIPTION_PLANS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sub Fee Amount</Label>
                    <Input value={formData.sub_fee_amount} onChange={(e) => updateField('sub_fee_amount', e.target.value)}
                      placeholder="Leave empty for campus default" className="border-[#EDE6D8] rounded-lg" data-testid="sub-fee-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Initial Recharge</Label>
                    <Input value={formData.recharge} onChange={(e) => updateField('recharge', e.target.value)}
                      placeholder="e.g., 1000000 (leave blank if none)" className="border-[#EDE6D8] rounded-lg" data-testid="sub-recharge-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Recharge Limit</Label>
                    <Input value={formData.min_recharge_limit} onChange={(e) => updateField('min_recharge_limit', e.target.value)}
                      placeholder="Default: 50000" className="border-[#EDE6D8] rounded-lg" data-testid="sub-min-recharge-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ambassador Registration</Label>
                    <Select value={formData.is_ambassador_reg} onValueChange={(v) => updateField('is_ambassador_reg', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-ambassador-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="N">No</SelectItem>
                        <SelectItem value="Y">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>WABA Registration</Label>
                    <Select value={formData.is_waba_reg} onValueChange={(v) => updateField('is_waba_reg', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-waba-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="N">No</SelectItem>
                        <SelectItem value="Y">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.is_waba_reg === 'Y' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>WA Reg No <span className="text-[#EF4444]">*</span></Label>
                      <Input value={formData.wa_reg_no} onChange={(e) => updateField('wa_reg_no', e.target.value)}
                        placeholder="WhatsApp registration number" className="border-[#EDE6D8] rounded-lg" data-testid="sub-wa-reg-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>WABA Reg Code <span className="text-[#EF4444]">*</span></Label>
                      <Input value={formData.waba_reg_code} onChange={(e) => updateField('waba_reg_code', e.target.value)}
                        placeholder="WABA registration code" className="border-[#EDE6D8] rounded-lg" data-testid="sub-waba-code-input" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Identification */}
            {step === 3 && !editingSubscriber && (
              <div className="space-y-4" data-testid="sub-step-identification">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Identification and agent details.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID Type</Label>
                    <Select value={formData.id_type} onValueChange={(v) => updateField('id_type', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="sub-id-type-select">
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        {idTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.id_name}</SelectItem>)}
                        {idTypes.length === 0 && <SelectItem value="1">National ID</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>ID Number</Label>
                    <Input value={formData.id_number} onChange={(e) => updateField('id_number', e.target.value)}
                      placeholder="e.g., 2974599009" className="border-[#EDE6D8] rounded-lg" data-testid="sub-id-number-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Relationship Agent</Label>
                  <Input value={formData.relationship_agent} onChange={(e) => updateField('relationship_agent', e.target.value)}
                    placeholder="Leave blank to use your username" className="border-[#EDE6D8] rounded-lg" data-testid="sub-agent-input" />
                </div>
              </div>
            )}

            {/* Navigation Footer */}
            <DialogFooter className="pt-6 mt-6 border-t border-[#EDE6D8] flex justify-between">
              <div>
                {step > 0 && !editingSubscriber && (
                  <Button type="button" variant="outline" onClick={() => setStep(step - 1)}
                    className="border-[#EDE6D8] rounded-lg" data-testid="sub-step-back-btn">
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#EDE6D8] rounded-lg">
                  Cancel
                </Button>
                {!editingSubscriber && step < formSteps.length - 1 ? (
                  <Button type="button" onClick={() => {
                    if (step === 0 && (!formData.sub_name || !formData.email || !formData.mobile)) {
                      toast.error('Name, email, and mobile are required');
                      return;
                    }
                    if (step === 1 && (!formData.campus_id || !formData.card_no)) {
                      toast.error('Campus and card number are required');
                      return;
                    }
                    setStep(step + 1);
                  }} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="sub-step-next-btn">
                    Next
                  </Button>
                ) : (
                  <Button type="submit" disabled={submitting}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="save-subscriber-btn">
                    {submitting ? 'Saving...' : (editingSubscriber ? 'Update' : 'Create')}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
